import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Info, Plus, Edit2, Trash2, X, Search } from 'lucide-react';
import api from '../services/api';

const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              color: '#6b7280',
              borderRadius: '6px'
            }}
          >
            <X size={24} />
          </button>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export const Groups = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [groupDetails, setGroupDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    members: []
  });

  // Entity search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Info tooltip state
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/groups');
      setGroups(response.data);
    } catch (err) {
      setError('Failed to load groups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = () => {
    setFormData({ name: '', description: '', members: [] });
    setShowCreateModal(true);
  };

  const handleEditGroup = async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}`);
      const group = response.data;
      setSelectedGroup(group);
      setFormData({
        name: group.name,
        description: group.description || '',
        members: group.members.map(m => ({
          committee_id: m.committee_id,
          committee_name: m.committee_name,
          mec_id: m.mec_id,
          label: m.label || '',
          notes: m.notes || ''
        }))
      });
      setShowEditModal(true);
    } catch (err) {
      alert('Failed to load group details');
      console.error(err);
    }
  };

  const handleDeleteGroup = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/groups/${selectedGroup.group_id}`);
      setShowDeleteModal(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (err) {
      alert('Failed to delete group');
      console.error(err);
    }
  };

  const fetchGroupDetail = async (groupId) => {
    if (groupDetails[groupId]) return groupDetails[groupId];
    setLoadingDetails(prev => ({ ...prev, [groupId]: true }));
    try {
      const response = await api.get(`/api/groups/${groupId}`);
      setGroupDetails(prev => ({ ...prev, [groupId]: response.data }));
      return response.data;
    } catch (err) {
      console.error('Error fetching group details:', err);
      return null;
    } finally {
      setLoadingDetails(prev => ({ ...prev, [groupId]: false }));
    }
  };

  const toggleGroup = async (groupId) => {
    const isExpanded = expandedGroups.includes(groupId);
    if (isExpanded) {
      setExpandedGroups(prev => prev.filter(id => id !== groupId));
      return;
    }
    await fetchGroupDetail(groupId);
    setExpandedGroups(prev => [...prev, groupId]);
  };

  const handleSearchCommittees = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await api.get(`/api/committees?search=${encodeURIComponent(query)}`);
      setSearchResults(response.data.committees || []);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddMember = (committee) => {
    // Check if already added
    if (formData.members.some(m => m.committee_id === committee.committee_id)) {
      return;
    }

    setFormData({
      ...formData,
      members: [...formData.members, {
        committee_id: committee.committee_id,
        committee_name: committee.committee_name,
        mec_id: committee.mec_id || '',
        label: '',
        notes: ''
      }]
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveMember = (committeeId) => {
    setFormData({
      ...formData,
      members: formData.members.filter(m => m.committee_id !== committeeId)
    });
  };

  const handleUpdateMember = (committeeId, field, value) => {
    setFormData({
      ...formData,
      members: formData.members.map(m =>
        m.committee_id === committeeId ? { ...m, [field]: value } : m
      )
    });
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        members: formData.members.map(m => ({
          committee_id: m.committee_id,
          label: m.label || null,
          notes: m.notes || null
        }))
      };
      await api.post('/api/groups', payload);
      setShowCreateModal(false);
      fetchGroups();
    } catch (err) {
      alert('Failed to create group');
      console.error(err);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        members: formData.members.map(m => ({
          committee_id: m.committee_id,
          label: m.label || null,
          notes: m.notes || null
        }))
      };
      await api.put(`/api/groups/${selectedGroup.group_id}`, payload);
      setShowEditModal(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (err) {
      alert('Failed to update group');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading groups...</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      {/* Page Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '2rem'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: '#111827',
                margin: 0
              }}>
                Reporting Entity Groups
              </h1>
              <div style={{ position: 'relative' }}>
                <Info
                  size={16}
                  style={{ cursor: 'pointer', color: '#6b7280' }}
                  onClick={() => setShowInfo(!showInfo)}
                  title="Groups let you analyze multiple reporting entities together in Analytics. Groups are flat and user-owned; they don’t change your underlying filings."
                />
                {showInfo && (
                  <div style={{
                    position: 'absolute',
                    top: '24px',
                  left: '0',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: '320px',
                  zIndex: 100
                }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5' }}>
                    Create groups of reporting entities (committees, PACs, candidates) to compare and analyze their campaign finance activity side-by-side.
                  </p>
                </div>
              )}
            </div>
          </div>
              <p style={{ color: '#6b7280', margin: 0 }}>
                Compare campaign finance activity across multiple entities
              </p>
              <p style={{ color: '#6b7280', margin: 0 }}>
                Create groups here, then select a group in Analytics to view combined charts across its members.
              </p>
            </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <Link
            to="/analytics"
            style={{
              color: '#4f46e5',
              textDecoration: 'none',
              fontWeight: '600',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              background: '#eef2ff',
              border: '1px solid #e0e7ff'
            }}
          >
            Back to Analytics
          </Link>
          <button
            onClick={handleCreateGroup}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(90deg, #2563eb, #4f46e5)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            <Plus size={20} />
            Create Group
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#991b1b'
        }}>
          {error}
        </div>
      )}

      {/* Groups List */}
      {groups.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No groups yet
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Create your first group to start comparing entities
          </p>
          <button
            onClick={handleCreateGroup}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(90deg, #2563eb, #4f46e5)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <Plus size={20} />
            Create Group
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {groups.map(group => (
            <div
              key={group.group_id}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              {(() => {
                const isExpanded = expandedGroups.includes(group.group_id);
                const detail = groupDetails[group.group_id];
                const loadingDetail = loadingDetails[group.group_id];

                return (
                  <>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleGroup(group.group_id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleGroup(group.group_id);
                        }
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.1rem', color: '#6b7280', marginTop: '0.2rem' }}>
                          {isExpanded ? '▾' : '▸'}
                        </span>
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: '1.25rem',
                            fontWeight: '700',
                            color: '#111827',
                            marginBottom: '0.35rem'
                          }}>
                            {group.name}
                          </h3>
                    {group.description && !isExpanded && (
                      <p style={{
                        color: '#6b7280',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {group.description}
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditGroup(group.group_id);
                          }}
                          style={{
                            padding: '0.5rem',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#374151'
                          }}
                          title="Edit group"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(group);
                          }}
                          style={{
                            padding: '0.5rem',
                            background: '#fef2f2',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                          title="Delete group"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      background: '#f9fafb',
                      borderRadius: '6px'
                    }}>
                      <span style={{ fontWeight: '600', color: '#374151' }}>
                        {group.member_count}
                      </span>
                      <span style={{ color: '#6b7280' }}>
                        {group.member_count === 1 ? 'reporting entity' : 'reporting entities'}
                      </span>
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: '1rem', display: 'grid', gap: '1rem' }}>
                        <div>
                          <h4 style={{ margin: '0 0 0.5rem 0', color: '#111827', fontSize: '1rem', fontWeight: '700' }}>Members</h4>
                          {loadingDetail && !detail && (
                            <p style={{ margin: 0, color: '#6b7280' }}>Loading members...</p>
                          )}
                          {detail?.members?.length > 0 ? (
                            <div style={{ display: 'grid', gap: '0.5rem' }}>
                              {detail.members.map((member, idx) => (
                                <div
                                  key={member.member_id || member.committee_id || idx}
                                  style={{ color: '#111827', paddingTop: idx === 0 ? 0 : '0.5rem', borderTop: idx === 0 ? 'none' : '1px solid #e5e7eb' }}
                                >
                                  <div style={{ fontWeight: 600 }}>{member.committee_name}</div>
                                  {member.mec_id && (
                                    <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                      {member.mec_id}
                                    </div>
                                  )}
                                  {member.label && (
                                    <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                                      Type: {member.label}
                                    </div>
                                  )}
                                  {member.notes && (
                                    <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                                      Notes: {member.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            !loadingDetail && <p style={{ margin: 0, color: '#6b7280' }}>No members listed.</p>
                          )}
                        </div>

                        <div>
                          <h4 style={{ margin: '0 0 0.5rem 0', color: '#111827', fontSize: '1rem', fontWeight: '700' }}>Notes</h4>
                          {detail?.description || group.description ? (
                            <p style={{ margin: 0, color: '#374151' }}>{detail?.description || group.description}</p>
                          ) : (
                            <p style={{ margin: 0, color: '#6b7280' }}>No notes added.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

            </div>
          ))}
        </div>
      )}

      {/* Create Group Modal */}
      <Modal show={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Group">
        <form onSubmit={handleSubmitCreate}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#374151'
            }}>
              Group Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem'
              }}
              placeholder="e.g., Springfield City Council Candidates 2024"
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#374151'
            }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
                minHeight: '80px',
                resize: 'vertical'
              }}
              placeholder="Optional description of this group"
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#374151'
            }}>
              Add Entities
            </label>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchCommittees(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
                placeholder="Search by committee name or MEC ID"
              />
            </div>

            {searchResults.length > 0 && (
              <div style={{
                marginTop: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {searchResults.map(committee => (
                  <div
                    key={committee.committee_id}
                    onClick={() => handleAddMember(committee)}
                    style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.target.style.background = 'white'}
                  >
                    <div style={{ fontWeight: '600', color: '#111827' }}>
                      {committee.committee_name}
                    </div>
                    {committee.mec_id && (
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        MEC ID: {committee.mec_id}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {formData.members.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#374151' }}>
                Selected Entities ({formData.members.length})
              </h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {formData.members.map(member => (
                  <div
                    key={member.committee_id}
                    style={{
                      padding: '1rem',
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.75rem'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#111827' }}>
                          {member.committee_name}
                        </div>
                        {member.mec_id && (
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {member.mec_id}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member.committee_id)}
                        style={{
                          padding: '0.25rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                      <input
                        type="text"
                        value={member.label}
                        onChange={(e) => handleUpdateMember(member.committee_id, 'label', e.target.value)}
                        placeholder="Label (e.g., Incumbent, Challenger)"
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          background: 'white'
                        }}
                      />
                      <textarea
                        value={member.notes}
                        onChange={(e) => handleUpdateMember(member.committee_id, 'notes', e.target.value)}
                        placeholder="Optional notes"
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          minHeight: '60px',
                          resize: 'vertical',
                          background: 'white'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#374151'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(90deg, #2563eb, #4f46e5)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Create Group
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Group Modal */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Group">
        <form onSubmit={handleSubmitEdit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#374151'
            }}>
              Group Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#374151'
            }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
                minHeight: '80px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#374151'
            }}>
              Add Entities
            </label>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchCommittees(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
                placeholder="Search by committee name or MEC ID"
              />
            </div>

            {searchResults.length > 0 && (
              <div style={{
                marginTop: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {searchResults.map(committee => (
                  <div
                    key={committee.committee_id}
                    onClick={() => handleAddMember(committee)}
                    style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.target.style.background = 'white'}
                  >
                    <div style={{ fontWeight: '600', color: '#111827' }}>
                      {committee.committee_name}
                    </div>
                    {committee.mec_id && (
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        MEC ID: {committee.mec_id}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {formData.members.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#374151' }}>
                Selected Entities ({formData.members.length})
              </h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {formData.members.map(member => (
                  <div
                    key={member.committee_id}
                    style={{
                      padding: '1rem',
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.75rem'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#111827' }}>
                          {member.committee_name}
                        </div>
                        {member.mec_id && (
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {member.mec_id}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member.committee_id)}
                        style={{
                          padding: '0.25rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                      <input
                        type="text"
                        value={member.label}
                        onChange={(e) => handleUpdateMember(member.committee_id, 'label', e.target.value)}
                        placeholder="Label (e.g., Incumbent, Challenger)"
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          background: 'white'
                        }}
                      />
                      <textarea
                        value={member.notes}
                        onChange={(e) => handleUpdateMember(member.committee_id, 'notes', e.target.value)}
                        placeholder="Optional notes"
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          minHeight: '60px',
                          resize: 'vertical',
                          background: 'white'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#374151'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(90deg, #2563eb, #4f46e5)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Group"
      >
        <p style={{ marginBottom: '1.5rem', color: '#374151' }}>
          Are you sure you want to delete <strong>{selectedGroup?.name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowDeleteModal(false)}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              color: '#374151'
            }}
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Delete Group
          </button>
        </div>
      </Modal>
    </div>
    <Footer />
    </>
  );
};
